<template>
  <NewBsBox :bs="{ display: 'inline-flex', ...bs }">
    <InputGroup
      ref="inputRef"
      :size="size"
      :bs="mergedBs"
      :input-bs="inputBs"
      :disabled="disabled"
      :value="modelValue"
      v-bind="$attrs"
      @input="handleInput"
      @keyup.enter="handleEnter"
    >
      <template #left>
        <!-- @slot 左边的 slot -->
        <slot name="left">
          <BeerIcon
            :size="size === 'l' ? 16 : 14"
            :bs="{ml: 12}"
            :icon="leftIcon"
            color="mute"
          />
        </slot>
      </template>
      <template #right>
        <slot name="right">
          <BeerIcon
            v-if="showClear && !disabled && modelValue"
            :size="size === 'l' ? 16 : 14"
            :bs="{mr: 12, cursor: 'pointer'}"
            icon="close-f"
            color="ash"
            @click="handleClear"
          />
        </slot>
      </template>
    </InputGroup>
    <BeerButton
      v-if="actionPosition !== 'none'"
      :loading="loading"
      v-bind="mergedActionsProps"
    >
      搜索
    </BeerButton>
  </NewBsBox>
</template>

<script lang="ts">
  import {
    defineComponent, PropType, computed, ref,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { merge } from 'lodash'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import InputGroup, { InputSize } from '../InputGroup/InputGroup.vue'
  import { BeerStyleObj } from '../../../core'

  type ActionPosition = 'inner' | 'outer' | 'none'

  /**
   * 内置 Search 样式扩展组件
   *
   * 样式不适用的特殊场景建议根据 InputGroup 或者 Input 扩展
   *
   * @see 继承InputGroup /beer-next/ui-blocks/data-entry/input-doc#InputGroup
   *
   */
  export default defineComponent({
    name: 'SearchInput',
    components: {
      InputGroup,
      BeerIcon,
      BeerButton,
      NewBsBox,
    },
    props: {
      /**
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      modelValue: {
        type: String,
        default: undefined,
        required: false,
      },
      /**
       * 是否禁止
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 大小样式,
       * @type {l|m}
       */
      size: {
        type: String as PropType<InputSize>,
        default: 'm',
      },
      /**
       * 显示 🔍️ 图标
       *
       * <slot name="left />" 会覆盖这个属性
       */
      leftIcon: {
        type: String,
        default: 'search',
      },
      /**
       * 是否显示 ❌️ 清除按钮
       *
       * <slot name="right />" 会覆盖这个属性
       */
      showClear: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否是加载中
       */
      loading: {
        type: Boolean,
        default: false,
      },
      /**
       * 搜索按钮的 button props
       */
      actionProps: {
        type: Object,
        default: undefined,
      },
      /**
       * 搜索按钮的位置
       * @type {inner|ounter}
       */
      actionPosition: {
        type: String as PropType<ActionPosition>,
        default: 'none',
      },
    },
    emits: {
      /**
       * 触发搜索事件
       */
      search: null,
      /**
       * 点击搜索按钮
       */
      'click:action': null,
      /**
       * 点击清除按钮
       */
      'click:clear': null,
      /**
       * 搜索文字更新
       */
      'update:modelValue': null,
    },
    setup(props, { emit }) {
      const inputRef = ref(null)
      const handleEnter = () => {
        /**
         * 回车，或者点击搜索按钮时触发的事件
         */
        emit('search')
      }

      const handleInput = (e: KeyboardEvent) => {
        emit('update:modelValue', (e.target as HTMLInputElement).value)
      }
      const handleClear = () => {
        emit('update:modelValue', '')
        emit('click:clear')
      }

      const mergedActionsProps = computed(() => {
        const bs: BeerStyleObj = {
          ml: '-1px',
          zIndex: 2,
          mr: 0,
        }
        if (props.size === 'm') {
          bs.minWidth = 'unset'
        }
        if (props.actionPosition === 'outer') {
          bs.ml = 8
        } else if (props.actionPosition === 'inner') {
          bs.borderTopLeftRadius = 0
          bs.borderBottomLeftRadius = 0
        }
        return merge({
          bs,
          handleClick: () => {
            emit('click:action')
            handleEnter()
          },
          size: props.size,
        }, props.actionProps)
      })
      const mergedBs = computed(() => {
        const bs: BeerStyleObj = {
          flex: '1 1 0',
        }
        if (
          props.actionProps?.variant
          && props.actionProps.variant !== 'default'
        ) {
          bs['&:hover, :focus-within'] = {
            zIndex: 3,
          }
        }
        return bs
      })
      const inputBs = computed(() => {
        const bs: BeerStyleObj = {}
        if (props.actionPosition === 'inner') {
          bs.borderTopRightRadius = 0
          bs.borderBottomRightRadius = 0
        }
        return bs
      })
      return {
        mergedActionsProps,
        mergedBs,
        inputBs,
        inputRef,
        handleEnter,
        handleClear,
        handleInput,
      }
    },
  })
</script>
